import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

// Find the nearest scrollable ancestor of a node (the element that actually
// scrolls). Falls back to the document scrolling element.
function getScrollParent(node) {
  let el = node && node.parentElement;
  while (el) {
    const style = window.getComputedStyle(el);
    const oy = style.overflowY;
    if ((oy === 'auto' || oy === 'scroll' || oy === 'overlay') && el.scrollHeight > el.clientHeight) {
      return el;
    }
    el = el.parentElement;
  }
  return document.scrollingElement || document.documentElement;
}

/**
 * useInfiniteList — JIRA-style infinite-scroll data loader.
 *
 * Loads the first page, then auto-loads the next page when a sentinel element
 * scrolls into view (attach `sentinelRef` to a div at the bottom of the list).
 * Server-side search/filter: pass `search` and/or `params`; when either changes
 * the list resets to page 1 and reloads. Search is debounced internally.
 *
 * The backend endpoint must return:
 *   { items, total, page, page_size, total_pages, has_more }
 *
 * Usage:
 *   const { items, total, loading, loadingMore, hasMore, sentinelRef, reload } =
 *     useInfiniteList({ endpoint: '/customers/paginated',
 *                       params: { status: 'active' },
 *                       search: searchTerm });
 *   ...list.map(...)...
 *   <div ref={sentinelRef} />
 *   <p>Showing {items.length} of {total}</p>
 *
 * @param {object}  cfg
 * @param {string}  cfg.endpoint   API path (relative to /api), e.g. '/sites/paginated'
 * @param {object}  [cfg.params]   Extra query params (filters). Reload on change.
 * @param {string}  [cfg.search]   Search term (debounced). Reload on change.
 * @param {number}  [cfg.pageSize] Items per page (default 50).
 * @param {number}  [cfg.debounceMs] Search debounce in ms (default 300).
 * @param {boolean} [cfg.enabled]  When false, does not fetch (default true).
 */
export function useInfiniteList({
  endpoint,
  params = {},
  search = '',
  pageSize = 50,
  debounceMs = 300,
  enabled = true,
}) {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const sentinelRef = useRef(null);
  const loadingRef = useRef(false);       // guards against overlapping fetches
  const pageRef = useRef(1);
  const reqIdRef = useRef(0);             // discards stale responses
  const paramsKey = JSON.stringify(params || {});
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  // Debounce the search term.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), debounceMs);
    return () => clearTimeout(t);
  }, [search, debounceMs]);

  const fetchPage = useCallback(async (targetPage, { append }) => {
    if (!enabled || !endpoint) return;
    loadingRef.current = true;
    const myReq = ++reqIdRef.current;
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const res = await api.get(endpoint, {
        params: { ...JSON.parse(paramsKey), search: debouncedSearch || undefined, page: targetPage, page_size: pageSize },
      });
      if (myReq !== reqIdRef.current) return; // a newer request superseded this
      const data = res.data || {};
      const newItems = data.items || [];
      setItems((prev) => (append ? [...prev, ...newItems] : newItems));
      setTotal(data.total || 0);
      setHasMore(Boolean(data.has_more));
      setPage(targetPage);
      pageRef.current = targetPage;
      setError(null);
    } catch (err) {
      if (myReq === reqIdRef.current) setError(err);
    } finally {
      if (myReq === reqIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
      loadingRef.current = false;
    }
  }, [endpoint, paramsKey, debouncedSearch, pageSize, enabled, api]);

  // Reset + load page 1 whenever endpoint / filters / search change.
  useEffect(() => {
    setItems([]);
    setPage(1);
    pageRef.current = 1;
    fetchPage(1, { append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (loadingRef.current || !hasMore) return;
    fetchPage(pageRef.current + 1, { append: true });
  }, [fetchPage, hasMore]);

  // Auto-load next page when the sentinel nears the bottom of whatever
  // element is actually scrolling (the app scrolls an INNER container, not the
  // window — so a viewport-rooted observer never fires). We detect the nearest
  // scrollable ancestor, use it as the observer root, and add a scroll-listener
  // fallback for layouts the observer can't cover.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;

    const scrollParent = getScrollParent(el);
    const isDocRoot =
      !scrollParent ||
      scrollParent === document.scrollingElement ||
      scrollParent === document.documentElement ||
      scrollParent === document.body;
    const rootEl = isDocRoot ? null : scrollParent;

    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { root: rootEl, rootMargin: '300px' },
    );
    observer.observe(el);

    const scrollTarget = rootEl || window;
    // Primary fallback: measure the scroll container directly.
    const onScroll = () => {
      const sc = rootEl || document.scrollingElement || document.documentElement;
      if (sc && sc.scrollHeight - sc.scrollTop - sc.clientHeight < 400) loadMore();
    };
    scrollTarget.addEventListener('scroll', onScroll, { passive: true });

    // Bulletproof fallback: scroll events don't bubble but DO fire in the
    // capture phase, so a document-level capture listener catches scrolling in
    // ANY nested container. We then check the sentinel's viewport position —
    // layout-agnostic (works whether the window or an inner div scrolls).
    const onAnyScroll = () => {
      const node = sentinelRef.current;
      if (!node) return;
      if (node.getBoundingClientRect().top <= window.innerHeight + 400) loadMore();
    };
    document.addEventListener('scroll', onAnyScroll, true);

    return () => {
      observer.disconnect();
      scrollTarget.removeEventListener('scroll', onScroll);
      document.removeEventListener('scroll', onAnyScroll, true);
    };
  }, [hasMore, loadMore, items.length]);

  // Auto-fill: if after a page loads the content still doesn't fill the
  // scroll area (e.g. tall screen / 50 short rows → no scrollbar → no scroll
  // events ever fire), keep loading the next page until it's scrollable or
  // there are no more pages. This is the common "stuck at 50" cause when the
  // list is shorter than the viewport.
  useEffect(() => {
    if (!hasMore || loading || loadingMore || loadingRef.current) return;
    const node = sentinelRef.current;
    if (!node) return;
    const id = window.setTimeout(() => {
      const n = sentinelRef.current;
      if (!n) return;
      // Keep filling until there are ~2 viewports of content below the top,
      // so the list is comfortably scrollable AND a normal scroll gesture is
      // guaranteed to reach the load-more trigger. A tight threshold left the
      // list "stuck at 50" when the document only slightly exceeded a tall
      // viewport (sentinel just below the fold, nothing fired until scroll).
      if (n.getBoundingClientRect().top <= window.innerHeight * 2) loadMore();
    }, 150);
    return () => window.clearTimeout(id);
  }, [items.length, hasMore, loading, loadingMore, loadMore]);

  const reload = useCallback(() => {
    setItems([]);
    setPage(1);
    pageRef.current = 1;
    fetchPage(1, { append: false });
  }, [fetchPage]);

  return { items, total, page, hasMore, loading, loadingMore, error, sentinelRef, loadMore, reload };
}

export default useInfiniteList;
